# Route: /materials/definitions/:id
# Read-only detail view for a material definition (ItemDefinition reference data).
# Audience: MES Engineers (md+), blocked on sm — same audience as def-list.
# Permission: ItemDefinition:read (GET /api/items/definitions/{definitionId})
# Feature: F75 #134 (AC-C3) — list rows navigate here via def-list row_action.

page-def-detail:
  type: Page
  archetype: detail
  meta:
    title: $t.def_detail.meta_title
    route: /materials/definitions/:id
    layout: shell
    auth: required
  data:
    definition:
      model: ItemDefinition
      key: $route.id
      required: true
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - def-detail-sm-notice
    - def-detail-content

# ── sm viewport notice ───────────────────────────────────────────────────────

def-detail-sm-notice:
  type: SmNotice
  content: $t.common.sm_notice_def

# ── Main content (hidden on sm) ──────────────────────────────────────────────

def-detail-content:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    responsive:
      sm:
        display: none
  children:
    - def-detail-header
    - def-detail-info

# ── Header: back-to-list (outlined secondary, from back_route) + name ────────
# Read-only page: no lifecycle actions. The PageHeaderTransform renders the
# back_route as a secondary (outlined) "← Back" button ($t.common.btn_back).

def-detail-header:
  type: PageHeader
  back_route: /materials/definitions
  title_prefix: $t.def_detail.title_prefix
  bind:
    content: definition.name

# ── Summary card: label/value grid per patterns.md § Detail page composition ─
# Note: ItemDefinitionDto exposes no version/status fields and no
# created/updated timestamps, so this card shows the full scalar read surface
# of GET /api/items/definitions/{id}. itemClassId is shown raw (mono), matching
# the def-list class column.

def-detail-info:
  type: Card
  spatial:
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - def-info-grid

def-info-grid:
  type: Grid
  spatial:
    columns: label-value
    gap: $spacing.md
    responsive:
      sm:
        columns: 1
  children:
    - def-info-name-label
    - def-info-name-value
    - def-info-id-label
    - def-info-id-value
    - def-info-class-label
    - def-info-class-value
    - def-info-tracking-label
    - def-info-tracking-value
    - def-info-unit-label
    - def-info-unit-value
    - def-info-shelf-life-label
    - def-info-shelf-life-value
    - def-info-make-buy-label
    - def-info-make-buy-value
    - def-info-description-label
    - def-info-description-value

def-info-name-label:
  type: Text
  content: $t.def_detail.label_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

def-info-name-value:
  type: Text
  bind:
    content: definition.name
  tokens:
    font_size: $font.size.md
    font_weight: "600"

def-info-id-label:
  type: Text
  content: $t.def_detail.label_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

def-info-id-value:
  type: Text
  bind:
    content: definition.id
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

def-info-class-label:
  type: Text
  content: $t.def_detail.label_class
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

def-info-class-value:
  type: Text
  bind:
    content: definition.itemClassId
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

def-info-tracking-label:
  type: Text
  content: $t.def_detail.label_tracking
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

def-info-tracking-value:
  type: Text
  bind:
    content: definition.trackingMode
  tokens:
    font_size: $font.size.md
    font_weight: "600"

def-info-unit-label:
  type: Text
  content: $t.def_detail.label_unit
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

def-info-unit-value:
  type: Text
  bind:
    content: definition.baseUnitOfMeasure
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

def-info-shelf-life-label:
  type: Text
  content: $t.def_detail.label_shelf_life
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

def-info-shelf-life-value:
  type: Text
  bind:
    content: definition.shelfLifeDays
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

def-info-make-buy-label:
  type: Text
  content: $t.def_detail.label_make_buy
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

def-info-make-buy-value:
  type: Text
  bind:
    content: definition.makeOrBuy
  tokens:
    font_size: $font.size.md
    font_weight: "600"

def-info-description-label:
  type: Text
  content: $t.def_detail.label_description
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

def-info-description-value:
  type: Text
  bind:
    content: definition.description
  tokens:
    font_size: $font.size.md
    font_weight: "600"
